import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { usePermission } from '@/permissions/usePermission';
import { useNotificationEffect } from '@/features/common/notifications/notificationBus';
import { NotificationCategory } from '@/api/types/notifications';
import type { ProviderFundsConfig } from './config';
import { SummaryTab } from './SummaryTab';
import { TransactionsTab } from './TransactionsTab';
import { DetailsTab } from './DetailsTab';
import { PeriodLockingTab } from './PeriodLockingTab';

interface Selected {
  providerId: string | null;
  providerName: string | null;
}

export function ProviderFundsPage({ config }: { config: ProviderFundsConfig }) {
  const can = usePermission();
  const { perms, key } = config;
  const queryClient = useQueryClient();

  // Background processing finished → refetch every tab's data for this page so the
  // grids/widgets reflect the newly processed transactions without a manual reload.
  // (All tab queries are keyed under [config.key]; the bell shows the toast.)
  useNotificationEffect((n) => {
    if (n.category === NotificationCategory.ProviderTransactionsProcessed) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  });

  const canViewRealCashBalance = can(perms.externalReconciliation);
  const canAddTransaction = can(perms.addTransactions);
  const canProcess = Boolean(perms.manualBalanceProcessing) && can(perms.manualBalanceProcessing);
  const canViewPeriodLocking = config.hasPeriodLocking && Boolean(perms.periodLocking) && can(perms.periodLocking);
  const canLock = Boolean(perms.lock) && can(perms.lock);
  const canUnlock = Boolean(perms.unlock) && can(perms.unlock);

  const listingKey = `${key}-listing`;
  const txKey = `${key}-transactions`;
  const detailsKey = `${key}-details`;
  const lockingKey = `${key}-locking`;

  const tabs: TabItem[] = useMemo(() => {
    const t: TabItem[] = [
      { key: listingKey, title: 'Providers Summary' },
      { key: txKey, title: 'Transactions' },
      { key: detailsKey, title: config.detailsTabTitle },
    ];
    if (canViewPeriodLocking) t.push({ key: lockingKey, title: 'Period Locking' });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewPeriodLocking, config.detailsTabTitle]);

  const [active, setActive] = useState(listingKey);
  const [selected, setSelected] = useState<Selected>({ providerId: null, providerName: null });

  function openTab(target: string, providerId: string, providerName: string) {
    setSelected({ providerId, providerName });
    setActive(target);
  }

  return (
    <div className={`${key}-provider-funds-page`}>
      <PageHeader title={config.title} breadcrumb={config.breadcrumb} />

      <div className="card custom-card mb-0">
        <div className="card-header p-0">
          <Tabs tabs={tabs} active={active} onChange={setActive} variant="underline" />
        </div>
        <div className="card-body">
          {active === listingKey && (
            <SummaryTab
              config={config}
              canViewRealCashBalance={canViewRealCashBalance}
              canViewPeriodLocking={canViewPeriodLocking}
              onOpenTransactions={(id, name) => openTab(txKey, id, name)}
              onOpenDetails={(id, name) => openTab(detailsKey, id, name)}
              onOpenLocking={(id, name) => openTab(lockingKey, id, name)}
            />
          )}
          {active === txKey && (
            <TransactionsTab
              config={config}
              canViewRealCashBalance={canViewRealCashBalance}
              canAddTransaction={canAddTransaction}
              selectedProviderId={selected.providerId}
              selectedProviderName={selected.providerName}
            />
          )}
          {active === detailsKey && (
            <DetailsTab config={config} canProcess={canProcess} selectedProviderId={selected.providerId} />
          )}
          {active === lockingKey && canViewPeriodLocking && (
            <PeriodLockingTab
              config={config}
              canViewRealCashBalance={canViewRealCashBalance}
              canLock={canLock}
              canUnlock={canUnlock}
              selectedProviderId={selected.providerId}
            />
          )}
        </div>
      </div>
    </div>
  );
}
